import { useCallback, useEffect, useRef, useState } from "react";

import type { MrpRfqLineInput, MrpRfq } from "@/lib/rfqApi";
import { patchMrpRfqHeader, replaceMrpRfqLines } from "@/lib/rfqApi";

import { rfqDraftSnapshotFromForm } from "./rfqComposeSessionCache";

const RFQ_DRAFT_AUTOSAVE_DEBOUNCE_MS = 400;
const RFQ_DRAFT_AUTOSAVE_SAVED_FADE_MS = 2000;

export type RfqDraftAutosaveStatus = "idle" | "saving" | "saved" | "error";

export function useRfqDraftAutosave(input: {
  enabled: boolean;
  formReady: boolean;
  rfq: MrpRfq | undefined;
  rfqId: number;
  internalNotes: string;
  externalNotes: string;
  neededByDate: string;
  currency: string;
  lines: MrpRfqLineInput[];
  savedDraftSnapshot: string | null;
  setSavedDraftSnapshot: (snapshot: string) => void;
  validateSubmitLines: (submitLines: MrpRfqLineInput[]) => void;
  normalizeSubmitLines: (lines: MrpRfqLineInput[]) => MrpRfqLineInput[];
  onPersisted: () => Promise<void>;
}): {
  draftAutosaveStatus: RfqDraftAutosaveStatus;
  draftAutosaveError: string | null;
  draftDirty: boolean;
  draftSavePending: boolean;
  draftSaveSucceeded: boolean;
  flushRfqDraftAutosave: () => Promise<void>;
  handleRfqDraftFieldBlur: () => void;
} {
  const autosaveTimerRef = useRef<number | null>(null);
  const autosaveInFlightRef = useRef(false);
  const autosavePendingRef = useRef(false);
  const persistChainRef = useRef(Promise.resolve());
  const lastAutosavePayloadRef = useRef<string | null>(null);
  const autosaveSavedFadeTimerRef = useRef<number | null>(null);
  const rfqDraftHydratedIdRef = useRef<number | null>(null);

  const [draftAutosaveStatus, setDraftAutosaveStatus] = useState<RfqDraftAutosaveStatus>("idle");
  const [draftAutosaveError, setDraftAutosaveError] = useState<string | null>(null);

  const draftPayloadSnapshot = rfqDraftSnapshotFromForm(
    input.internalNotes,
    input.externalNotes,
    input.neededByDate,
    input.currency,
    input.lines,
  );

  const draftDirty =
    input.enabled &&
    input.formReady &&
    input.savedDraftSnapshot != null &&
    draftPayloadSnapshot !== input.savedDraftSnapshot;

  const submitLinesForAutosave = input.normalizeSubmitLines(input.lines);

  useEffect(() => {
    if (!input.enabled || input.rfq == null) {
      rfqDraftHydratedIdRef.current = null;
      lastAutosavePayloadRef.current = null;
      return;
    }
    if (!input.formReady || input.savedDraftSnapshot == null) {
      return;
    }
    if (rfqDraftHydratedIdRef.current !== input.rfq.id) {
      rfqDraftHydratedIdRef.current = input.rfq.id;
      setDraftAutosaveStatus("idle");
      setDraftAutosaveError(null);
    }
    if (draftPayloadSnapshot === input.savedDraftSnapshot) {
      lastAutosavePayloadRef.current = draftPayloadSnapshot;
    }
  }, [
    draftPayloadSnapshot,
    input.enabled,
    input.formReady,
    input.rfq,
    input.rfq?.id,
    input.savedDraftSnapshot,
  ]);

  const markDraftAutosaveSaved = useCallback(() => {
    if (autosaveSavedFadeTimerRef.current != null) {
      window.clearTimeout(autosaveSavedFadeTimerRef.current);
    }
    setDraftAutosaveStatus("saved");
    setDraftAutosaveError(null);
    autosaveSavedFadeTimerRef.current = window.setTimeout(() => {
      autosaveSavedFadeTimerRef.current = null;
      setDraftAutosaveStatus("idle");
    }, RFQ_DRAFT_AUTOSAVE_SAVED_FADE_MS);
  }, []);

  const flushRfqDraftAutosave = useCallback(async (): Promise<void> => {
    if (!input.enabled || !input.formReady || input.rfq == null || input.rfq.status !== "draft") {
      return;
    }
    if (rfqDraftHydratedIdRef.current !== input.rfq.id) {
      return;
    }
    if (draftPayloadSnapshot === input.savedDraftSnapshot) {
      return;
    }
    if (draftPayloadSnapshot === lastAutosavePayloadRef.current) {
      return;
    }
    if (autosaveInFlightRef.current) {
      autosavePendingRef.current = true;
      return;
    }

    const submitLines = submitLinesForAutosave;
    try {
      input.validateSubmitLines(submitLines);
    } catch {
      return;
    }

    autosaveInFlightRef.current = true;
    if (autosaveSavedFadeTimerRef.current != null) {
      window.clearTimeout(autosaveSavedFadeTimerRef.current);
      autosaveSavedFadeTimerRef.current = null;
    }
    setDraftAutosaveStatus("saving");
    setDraftAutosaveError(null);

    try {
      await patchMrpRfqHeader(input.rfqId, {
        internal_notes: input.internalNotes.trim() || null,
        external_notes: input.externalNotes.trim() || null,
        needed_by_date: input.neededByDate.trim() || null,
        currency: input.currency,
      });
      await replaceMrpRfqLines(input.rfqId, submitLines);
      lastAutosavePayloadRef.current = draftPayloadSnapshot;
      input.setSavedDraftSnapshot(draftPayloadSnapshot);
      markDraftAutosaveSaved();
      await input.onPersisted();
    } catch (error) {
      lastAutosavePayloadRef.current = null;
      setDraftAutosaveStatus("error");
      setDraftAutosaveError(error instanceof Error ? error.message : "Could not save this RFQ.");
    } finally {
      autosaveInFlightRef.current = false;
      if (autosavePendingRef.current) {
        autosavePendingRef.current = false;
        void flushRfqDraftAutosave();
      }
    }
  }, [
    draftPayloadSnapshot,
    input.currency,
    input.enabled,
    input.externalNotes,
    input.formReady,
    input.internalNotes,
    input.lines,
    input.neededByDate,
    input.normalizeSubmitLines,
    input.onPersisted,
    input.rfq,
    input.rfqId,
    input.savedDraftSnapshot,
    input.setSavedDraftSnapshot,
    input.validateSubmitLines,
    markDraftAutosaveSaved,
    submitLinesForAutosave,
  ]);

  const enqueueRfqDraftAutosave = useCallback((): Promise<void> => {
    const next = persistChainRef.current.then(
      () => flushRfqDraftAutosave(),
      () => flushRfqDraftAutosave(),
    );
    persistChainRef.current = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }, [flushRfqDraftAutosave]);

  const scheduleRfqDraftAutosave = useCallback(() => {
    if (!input.enabled) {
      return;
    }
    if (autosaveTimerRef.current != null) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void enqueueRfqDraftAutosave();
    }, RFQ_DRAFT_AUTOSAVE_DEBOUNCE_MS);
  }, [enqueueRfqDraftAutosave, input.enabled]);

  const handleRfqDraftFieldBlur = useCallback(() => {
    if (!input.enabled) {
      return;
    }
    if (autosaveTimerRef.current != null) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    void enqueueRfqDraftAutosave();
  }, [enqueueRfqDraftAutosave, input.enabled]);

  useEffect(() => {
    if (!input.enabled || !input.formReady || input.rfq == null) {
      return;
    }
    if (rfqDraftHydratedIdRef.current !== input.rfq.id) {
      return;
    }
    if (draftPayloadSnapshot === input.savedDraftSnapshot) {
      return;
    }
    scheduleRfqDraftAutosave();
  }, [
    draftPayloadSnapshot,
    input.enabled,
    input.formReady,
    input.rfq?.id,
    input.savedDraftSnapshot,
    scheduleRfqDraftAutosave,
    input.rfq,
  ]);

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current != null) {
        window.clearTimeout(autosaveTimerRef.current);
      }
      if (autosaveSavedFadeTimerRef.current != null) {
        window.clearTimeout(autosaveSavedFadeTimerRef.current);
      }
    };
  }, []);

  return {
    draftAutosaveStatus,
    draftAutosaveError,
    draftDirty,
    draftSavePending: draftAutosaveStatus === "saving",
    draftSaveSucceeded: draftAutosaveStatus === "saved",
    flushRfqDraftAutosave: enqueueRfqDraftAutosave,
    handleRfqDraftFieldBlur,
  };
}
